import React from 'react';
import PropTypes from 'prop-types';
import { Meteor } from 'meteor/meteor';
import { withTracker } from 'meteor/react-meteor-data';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isEmpty } from 'lodash';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import methodPromise from '../../../modules/method-promise-wrapper';
import Annotations from '../../../api/Annotations/Annotations';
import Loading from '../Loading';

const ItemType = {
  TASK: 'task'
};

const DraggableTask = ({ task, index }) => {
  const [, drag] = useDrag(() => ({
    type: ItemType.TASK,
    item: { id: task.id, index, column: task.column }
  }));

  return (
    <Card
      ref={drag}
      sx={{
        marginBottom: 2,
        cursor: 'grab',
        boxShadow: 2,
        backgroundColor: '#fff'
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" sx={{ marginBottom: 1 }}>
          {task.libraryIcon === 'fontawesome' && task.icon && (
            <FontAwesomeIcon icon={task.icon} style={{ marginRight: '8px', fontSize: '20px' }} />
          )}
          {task.libraryIcon === 'svg' && task.icon && (
            <img
              src={task.icon}
              alt={task.templateName || 'Icon'}
              style={{ marginRight: '8px', width: '20px', height: '20px' }}
            />
          )}
          <Typography variant="body1" component="div">
            {task.templateName || task.name}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary">
          {task.date && `Date: ${task.date}`}
          {task.time && ` | Time: ${task.time}`}
        </Typography>
      </CardContent>
    </Card>
  );
};

DraggableTask.propTypes = {
  task: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired
};

const DroppableColumn = ({ columnName, tasks, moveTask }) => {
  const [, drop] = useDrop(() => ({
    accept: ItemType.TASK,
    drop: item => {
      moveTask(item, columnName);
    }
  }));

  return (
    <Box
      ref={drop}
      sx={{
        width: 300,
        minHeight: 400,
        overflowY: 'auto',
        border: '1px solid #ccc',
        borderRadius: 2,
        background: '#f9f9f9',
        padding: 2
      }}
    >
      <Typography variant="h6" sx={{ textAlign: 'center', marginBottom: 2 }}>
        {columnName}
      </Typography>
      {tasks.map((task, index) => (
        <DraggableTask key={task.id} task={task} index={index} moveTask={moveTask} />
      ))}
    </Box>
  );
};

DroppableColumn.propTypes = {
  columnName: PropTypes.string.isRequired,
  tasks: PropTypes.array.isRequired,
  moveTask: PropTypes.func.isRequired
};

const TableAnnotationsComponent = ({ annotationList, isloaded }) => {
  if (!isloaded) return <Loading />;

  const newTasks = annotationList.flatMap(annotation => {
    const { template } = annotation.geoJSON.properties || {};
    if (!template || !template.elements) return [];
    return template.elements
      .filter(el => el.elementType === 'select')
      .map(el => ({
        id: `${annotation._id}-${el.order}`,
        name: el.labelName,
        column: el.value || '',
        elementOrder: el.order,
        annotationId: annotation._id,
        templateName: template.name,
        userName: (template.elements.find(e => e.labelName === 'User') || {}).value,
        date: (template.elements.find(e => e.labelName === 'Date') || {}).value,
        time: (template.elements.find(e => e.labelName === 'Time') || {}).value,
        libraryIcon: template.libraryIcon,
        icon: template.libraryIcon === 'fontawesome' ? template.icon : template.iconSvg
      }));
  });

  const [tasks, setTasks] = React.useState(newTasks);
  const [openModal, setOpenModal] = React.useState(false);

  const moveTask = async (item, destinationColumn) => {
    setTasks(prevTasks => {
      const updatedTasks = prevTasks.map(task => {
        if (task.id === item.id) {
          // eslint-disable-next-line no-param-reassign
          task.column = destinationColumn;
        }
        return task;
      });
      return updatedTasks;
    });

    const movedTask = tasks.find(task => task.id === item.id);

    try {
      await methodPromise(
        'annotations.updateElementValue',
        movedTask.annotationId,
        movedTask.elementOrder,
        destinationColumn
      );
    } catch (err) {
      console.error('Error updating annotation:', err);
    }
  };

  const groupedTasks = React.useMemo(() => {
    const groups = {};
    const columns =
      annotationList[0].geoJSON.properties.template.elements.find(el => el.elementType === 'select')
        .optionList || [];

    columns.forEach(column => {
      groups[column] = tasks.filter(task => task.column === column);
    });

    return groups;
  }, [tasks, annotationList]);

  const tableColumns = [
    { field: 'id', headerName: 'ID', flex: 1 },
    { field: 'name', headerName: 'Name', flex: 1 },
    { field: 'column', headerName: 'Column', flex: 1 },
    { field: 'elementOrder', headerName: 'Order', flex: 1 },
    { field: 'templateName', headerName: 'Template Name', flex: 1 },
    { field: 'userName', headerName: 'User Name', flex: 1 },
    { field: 'date', headerName: 'Date', flex: 1 },
    { field: 'time', headerName: 'Time', flex: 1 }
  ];

  const openTableModal = () => setOpenModal(true);
  const closeTableModal = () => setOpenModal(false);

  React.useEffect(() => {
    if (!isEmpty(annotationList)) {
      const updatedTasks = annotationList.flatMap(annotation => {
        const { template } = annotation.geoJSON.properties;
        return template.elements
          .filter(el => el.elementType === 'select')
          .map(el => ({
            id: `${annotation._id}-${el.order}`,
            name: el.labelName,
            column: el.value || '',
            elementOrder: el.order,
            annotationId: annotation._id,
            templateName: template.name,
            userName: (template.elements.find(e => e.labelName === 'User') || {}).value,
            date: (template.elements.find(e => e.labelName === 'Date') || {}).value,
            time: (template.elements.find(e => e.labelName === 'Time') || {}).value,
            libraryIcon: template.libraryIcon,
            icon: template.libraryIcon === 'fontawesome' ? template.icon : template.iconSvg
          }));
      });
      setTasks(updatedTasks);
    }
  }, [annotationList]);

  return (
    <DndProvider backend={HTML5Backend}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, padding: 2, height: '100%' }}>
        <Button
          variant="outlined"
          sx={{ color: '#2A3F90', minWidth: 'auto', padding: '4px 8px' }}
          onClick={openTableModal}
          size="small"
        >
          Open Data Table
        </Button>
        <Box
          sx={{ display: 'flex', gap: 2, justifyContent: 'center', padding: 2, maxHeight: '95%' }}
        >
          {Object.entries(groupedTasks).map(([columnName, columnTasks]) => (
            <DroppableColumn
              key={columnName}
              columnName={columnName}
              tasks={columnTasks}
              moveTask={moveTask}
            />
          ))}
        </Box>
        <Dialog open={openModal} onClose={closeTableModal} fullWidth maxWidth="lg">
          <DialogTitle>Data Table</DialogTitle>
          <DialogContent>
            <Box sx={{ height: 500, width: '100%' }}>
              <DataGridPremium
                rows={tasks}
                columns={tableColumns}
                getRowId={row => row.id}
                pageSize={10}
                rowsPerPageOptions={[10, 20, 50]}
                disableSelectionOnClick
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeTableModal} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </DndProvider>
  );
};

TableAnnotationsComponent.propTypes = {
  annotationList: PropTypes.array.isRequired,
  isloaded: PropTypes.bool.isRequired
};

export default withTracker(({ tableAnnotations }) => {
  const annotationSub = Meteor.subscribe('annotations.video', tableAnnotations.videoId);
  const annotationList = Annotations.find({}, { sort: { videoTime: 1 } }).fetch();
  const isloaded = annotationSub.ready() && !!annotationList;

  return {
    annotationList,
    isloaded
  };
})(TableAnnotationsComponent);
